if (!customElements.get('collection-grid')) {
  class CollectionGrid extends HTMLElement {
    constructor() {
      super();
      this.bindOnButtonClick = this.onButtonClick.bind(this);
      this._appliedGridView = null;
      this._gridViewUnsub = null;
      this.handleGridViewChange = this.handleGridViewChange.bind(this);
      this.pageController = new AbortController();
      this.pageRequests = new Map();
      this.unsubscribers = [];
    }

    connectedCallback() {
      this.init();
      this.handlePLPScrollRestoration();
      this.initGridViewListener();
      this.subscribeToFilterEvents();
    }

    disconnectedCallback() {
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.prefetchObserver) this.prefetchObserver.disconnect();
      if (this.pageObserver) this.pageObserver.disconnect();
      this._gridViewUnsub?.();
      document.removeEventListener('collection-grid-view:change', this.handleGridViewChange);
      this.unsubscribers.forEach((unsubscribe) => unsubscribe());
      this.unsubscribers = [];
      this.resetPageRequests();
    }

    subscribeToFilterEvents() {
      const events = window.theme?.pubsub?.events;
      if (!events) return;

      [events.FILTER_APPLIED, events.FILTER_CLEARED, events.SORT_CHANGED].forEach((eventName) => {
        this.unsubscribers.push(theme.pubsub.subscribe(eventName, () => this.resetPageRequests()));
      });
    }

    resetPageRequests() {
      this.pageController.abort();
      this.pageController = new AbortController();
      this.pageRequests.clear();
    }

    requestPage(targetUrl) {
      const cached = this.pageRequests.get(targetUrl);
      if (cached && Date.now() - cached.at < theme.cache.TTL_FRESH) return cached.promise;

      const { signal } = this.pageController;
      const promise = fetch(targetUrl, { signal }).then((response) => {
        if (!response.ok) throw new Error(`Page request failed: ${response.status}`);
        return response.text();
      });

      const entry = { promise, at: Date.now() };
      this.pageRequests.set(targetUrl, entry);
      promise.catch(() => {
        if (this.pageRequests.get(targetUrl) === entry) this.pageRequests.delete(targetUrl);
      });

      return promise;
    }

    prefetchPage(targetUrl) {
      if (!targetUrl || this.pageRequests.has(targetUrl) || navigator.connection?.saveData) return;
      this.requestPage(targetUrl).catch(() => {});
    }

    init() {
      if (this.dataset.paginationType === 'pages') return;

      this.loadingPage = false;
      this.currentPage = +this.dataset.currentPage;
      this.productGrid = this.querySelector('#product-grid');
      if (!this.productGrid) return;

      this.cardSelector = this.productGrid.classList.contains('grill') ? '.grill__item' : '.grid__item';
      this.cardPageElements = Array.from(this.querySelectorAll(`${this.cardSelector}[data-current-page]`));
      this.currentPages = {};
      this.updateCurrentPages();

      this.automaticTriggers = this.querySelectorAll('.collection-grid__automatic');
      this.automaticTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.bindOnButtonClick);
      });

      if (this.dataset.paginationType === 'click') {
        this.nextButton = this.querySelector('.collection-grid__manual');
        if (this.nextButton) this.nextButton.addEventListener('click', this.bindOnButtonClick);
      }

      this.loadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.loadingPage) {
              const trigger = entry.target;
              const target = trigger.href || trigger.dataset.target;
              this.setLoadingTrigger(trigger, true);
              this.loadPage(target, trigger);
            }
          });
        },
        { rootMargin: '300px 0px' }
      );
      this.prefetchObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const trigger = entry.target;
            this.prefetchObserver.unobserve(trigger);
            this.prefetchPage(trigger.href || trigger.dataset.target);
          });
        },
        { rootMargin: '100% 0px' }
      );
      this.observeAutomaticTriggers();

      this.pageObserver = new IntersectionObserver(
        () => this.syncPageUrl(),
        { threshold: 0, rootMargin: '-50% 0px -50% 0px' }
      );
      this.observePageBoundaries();
    }

    updateCurrentPages() {
      this.cardPageElements = Array.from(this.querySelectorAll(`${this.cardSelector}[data-current-page]`));
      this.currentPages = {};

      this.cardPageElements.forEach((element) => {
        const pageKey = element.dataset.currentPage;

        if (!this.currentPages[pageKey]) {
          this.currentPages[pageKey] = { url: '', page: null, first: null, last: null };
        }

        this.currentPages[pageKey].url = element.dataset.currentPageUrl;
        this.currentPages[pageKey].page = +element.dataset.currentPage;

        if (element.hasAttribute('last-from-page')) {
          this.currentPages[pageKey].last = element;
        } else if (element.hasAttribute('first-from-page')) {
          this.currentPages[pageKey].first = element;
        }
      });
    }

    loadPage(targetUrl, trigger) {
      if (!targetUrl) {
        this.loadingPage = false;
        return;
      }

      this.loadingPage = true;
      const isPrev = trigger.id === 'PrevPageTrigger';

      this.requestPage(targetUrl)
        .then((responseText) => {
          this.pageRequests.delete(targetUrl);

          if (!this.isConnected || !trigger.isConnected) return;

          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

          const currentScroll = document.documentElement.scrollHeight - window.scrollY;
          const currentScrollY = window.scrollY;
          const scrollOffsetY = 300;

          let newProductCards = Array.from(responseHTML.querySelectorAll(`collection-grid ${this.cardSelector}`));
          const newTrigger = responseHTML.getElementById(trigger.id);

          if (isPrev) {
            newProductCards = newProductCards.reverse();
            newProductCards.forEach((card) => {
              this.productGrid.prepend(card);
            });

            window.scrollTo({
              top: document.documentElement.scrollHeight - currentScroll,
              behavior: 'instant',
            });
          } else {
            newProductCards.forEach((card) => {
              this.productGrid.appendChild(card);
            });

            const bottomPage = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight;
            if (bottomPage) {
              window.scrollTo({
                top: currentScrollY - scrollOffsetY,
                behavior: 'instant',
              });
            }
          }

          if (newTrigger) {
            newTrigger.addEventListener('click', this.bindOnButtonClick);
            trigger.replaceWith(newTrigger);
          } else {
            trigger.remove();
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') console.error(err);
          this.setLoadingTrigger(trigger, false);
        })
        .finally(() => {
          this.updateCurrentPages();
          this.observeAutomaticTriggers();
          this.observePageBoundaries();
          this.setLoadingTrigger(trigger, false);
          this.loadingPage = false;
        });
    }

    onButtonClick(event) {
      event.preventDefault();
      if (this.loadingPage) return;

      const button = event.currentTarget;
      const target = button.href || button.dataset.target;

      this.setLoadingTrigger(button, true);
      this.loadPage(target, button);
    }

    setLoadingTrigger(opener, enableLoading) {
      if (!opener) return;

      const spinner = opener.querySelector('.loading__spinner');
      if (!spinner) return;

      if (enableLoading) {
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        spinner.classList.remove('hidden');
      } else {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        spinner.classList.add('hidden');
      }
    }

    observeAutomaticTriggers() {
      if (!this.loadObserver) return;
      this.loadObserver.disconnect();
      this.prefetchObserver?.disconnect();
      this.automaticTriggers = this.querySelectorAll('.collection-grid__automatic');
      this.automaticTriggers.forEach((trigger) => {
        this.loadObserver.observe(trigger);
        this.prefetchObserver?.observe(trigger);
      });
    }

    observePageBoundaries() {
      if (!this.pageObserver) return;
      this.pageObserver.disconnect();
      this.querySelectorAll(`${this.cardSelector}[first-from-page]`).forEach((el) => {
        this.pageObserver.observe(el);
      });
    }

    syncPageUrl() {
      const viewportCenter = window.scrollY + window.innerHeight / 2;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10;
      let best = null;

      Object.values(this.currentPages).forEach((page) => {
        if (!page.first) return;
        if (atBottom || page.first.getBoundingClientRect().top + window.scrollY <= viewportCenter) {
          if (!best || page.page > best.page) best = page;
        }
      });

      if (best && best.page !== this.currentPage) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', `${best.page}`);
        window.history.replaceState({ page: best.page }, '', url);
        this.currentPage = best.page;
      }
    }

    handlePLPScrollRestoration() {
      const isSafariMobile =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
        /iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (!isSafariMobile) return;

      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('plp-scroll', window.scrollY);
      });

      document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (link && link.href && link.href.includes('/products/')) {
          sessionStorage.setItem('plp-scroll', window.scrollY);
        }
      });

      window.addEventListener('pageshow', (event) => {
        const scroll = sessionStorage.getItem('plp-scroll');
        if (event.persisted && scroll !== null) {
          setTimeout(() => window.scrollTo(0, parseInt(scroll, 10)), 100);
          sessionStorage.removeItem('plp-scroll');
        } else if (!event.persisted) {
          sessionStorage.removeItem('plp-scroll');
        }
      });
    }

    initGridViewListener() {
      const viewSelector = document.querySelector('[data-grid-view-selector]');
      if (!viewSelector) return;

      if (!this.productGrid) {
        this.productGrid = this.querySelector('#product-grid');
      }

      if (window.theme?.pubsub) {
        this._gridViewUnsub = theme.pubsub.subscribe('collection-grid-view:change', this.handleGridViewChange);
      } else {
        document.addEventListener('collection-grid-view:change', this.handleGridViewChange);
      }

      const savedView = localStorage.getItem('collection-grid-view') || '4';
      const alreadyApplied = this.productGrid?.classList.contains(`collection__grid--view-${savedView}`);
      this._appliedGridView = alreadyApplied ? savedView : null;
      if (!alreadyApplied) this.updateGridView(savedView);
    }

    handleGridViewChange(event) {
      const view = event.detail?.view || event.view;
      if (view) this.updateGridView(view);
    }

    updateGridView(view) {
      if (view === this._appliedGridView) return;

      if (!this.productGrid) {
        this.productGrid = this.querySelector('#product-grid');
      }
      if (!this.productGrid) return;

      this._appliedGridView = view;

      const applyView = () => this.applyGridView(view);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (document.startViewTransition && !prefersReducedMotion) {
        document.startViewTransition(applyView);
      } else {
        applyView();
      }
    }

    applyGridView(view) {
      const base = parseInt(this.dataset.columnsDesktop, 10) || 4;

      this.productGrid.classList.remove('collection__grid--view-2', 'collection__grid--view-4', 'collection__grid--view-12');
      this.productGrid.classList.add(`collection__grid--view-${view}`);

      if (view === '12') {
        this.productGrid.style.setProperty('--columns-mobile', '2');
        this.productGrid.style.setProperty('--columns-tablet', '12');
        this.productGrid.style.setProperty('--columns-desktop', '12');
      } else if (view === '2') {
        const compact = Math.ceil(base / 2);
        this.productGrid.style.setProperty('--columns-mobile', '1');
        this.productGrid.style.setProperty('--columns-tablet', String(compact));
        this.productGrid.style.setProperty('--columns-desktop', String(compact));
      } else if (view === '4') {
        const tablet = parseInt(this.dataset.columnsTablet, 10) || Math.max(base - 1, 2);
        this.productGrid.style.setProperty('--columns-mobile', '2');
        this.productGrid.style.setProperty('--columns-tablet', String(tablet));
        this.productGrid.style.setProperty('--columns-desktop', String(base));
      }
    }
  }

  customElements.define('collection-grid', CollectionGrid);
}
